'use client';

import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const TICKER_TEXT =
  'Expanded bilingual course materials are being added daily. Stay tuned for the full launch of our revised and enhanced curriculum in June.';

const STATS = [
  { value: '50+', label: 'Courses', sub: 'Expert-led fisheries training' },
  { value: '20,000+', label: 'Learners', sub: 'Across the fisheries sector' },
  { value: '28,000+', label: 'Certificates', sub: 'Issued & blockchain-verified' },
  { value: '4', label: 'Audiences', sub: 'Farmers, Students, Entrepreneurs, Technicians' },
];

const FEATURES = [
  {
    icon: 'AcademicCapIcon',
    title: 'Structured Learning Pathways',
    desc: 'Role-specific curricula designed with fisheries experts — from aquaculture basics to advanced enterprise management.',
    color: 'bg-blue-50 text-blue-700',
  },
  {
    icon: 'GlobeAltIcon',
    title: 'Multilingual Course Content',
    desc: 'All materials available in English and major Indian regional languages, ensuring no learner is left behind regardless of language preference.',
    color: 'bg-teal-50 text-teal-700',
  },
  {
    icon: 'ShieldCheckIcon',
    title: 'Blockchain-Verified Certificates',
    desc: 'Every certificate issued is tamper-proof and verifiable on the Polygon network — credibility you can prove anywhere.',
    color: 'bg-purple-50 text-purple-700',
  },
  {
    icon: 'ChartBarIcon',
    title: 'Market Access & Price Intelligence',
    desc: 'Real-time buyer connections, price trend data, and market opportunity alerts to help you sell smarter.',
    color: 'bg-amber-50 text-amber-700',
  },
  {
    icon: 'UserGroupIcon',
    title: 'Community & Mentorship',
    desc: 'Connect with industry mentors, join peer groups, and participate in live training events and webinars.',
    color: 'bg-emerald-50 text-emerald-700',
  },
  {
    icon: 'DevicePhoneMobileIcon',
    title: 'Personalized Dashboard',
    desc: 'Your progress, recommendations, certifications, and market alerts — all in one adaptive, role-aware interface.',
    color: 'bg-orange-50 text-orange-700',
  },
];

const AUDIENCES = [
  {
    role: 'Fish Farmers',
    icon: '🐟',
    desc: 'Learn sustainable aquaculture practices, pond management, disease prevention, and post-harvest handling to increase yield and income.',
    href: '/fisheries-courses',
    color: 'border-emerald-300 bg-emerald-50',
    badge: 'bg-emerald-100 text-emerald-700',
  },
  {
    role: 'Students',
    icon: '🎓',
    desc: 'Build a career in fisheries with accredited courses, mentorship from industry professionals, and certificates recognized by employers.',
    href: '/fisheries-courses',
    color: 'border-blue-300 bg-blue-50',
    badge: 'bg-blue-100 text-blue-700',
  },
  {
    role: 'Entrepreneurs',
    icon: '📈',
    desc: 'Scale your fisheries business with training in value-chain management, digital marketing, export compliance, and market access tools.',
    href: '/digital-marketplace',
    color: 'border-orange-300 bg-orange-50',
    badge: 'bg-orange-100 text-orange-700',
  },
  {
    role: 'Technicians',
    icon: '🔬',
    desc: 'Advance your technical expertise in water quality, feed formulation, hatchery operations, and equipment maintenance.',
    href: '/capacity-building-hub',
    color: 'border-purple-300 bg-purple-50',
    badge: 'bg-purple-100 text-purple-700',
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-background">
      {/* ── Red Ticker ── */}
      <div className="bg-red-600 text-white overflow-hidden py-2.5">
        <div className="flex whitespace-nowrap">
          <span
            className="inline-block animate-ticker text-sm font-semibold tracking-wide px-8"
          >
            📢 &nbsp;{TICKER_TEXT}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;📢 &nbsp;{TICKER_TEXT}&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;📢 &nbsp;{TICKER_TEXT}
          </span>
        </div>
      </div>

      {/* ── Header / Nav ── */}
      <nav className="bg-card shadow-sm sticky top-0 z-40 border-b border-border">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center">
              <svg width="22" height="22" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2C7.373 2 2 7.373 2 14s5.373 12 12 12 12-5.373 12-12S20.627 2 14 2zm0 3c1.657 0 3 1.343 3 3s-1.343 3-3 3-3-1.343-3-3 1.343-3 3-3zm0 16c-3.314 0-6-2.686-6-6 0-.768.156-1.5.438-2.172C9.844 13.594 11.782 14 14 14s4.156-.406 5.562-1.172c.282.672.438 1.404.438 2.172 0 3.314-2.686 6-6 6z" fill="white"/>
              </svg>
            </div>
            <div>
              <span className="text-lg font-headline font-bold text-foreground">FISCMAP</span>
              <span className="text-xs text-muted-foreground block leading-none">Portal</span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link href="/login" className="text-sm font-semibold text-foreground hover:text-primary transition-colors px-4 py-2 rounded-lg hover:bg-muted">
              Sign In
            </Link>
            <Link href="/signup" className="text-sm font-semibold bg-primary text-white px-5 py-2 rounded-lg hover:bg-primary/90 transition-colors shadow-sm">
              Get Started Free
            </Link>
          </div>
        </div>
      </nav>

      {/* ── Hero ── */}
      <section className="relative bg-gradient-to-br from-primary via-[#1e5f7a] to-ocean-teal text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 rounded-full bg-white/20 blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-accent/30 blur-3xl" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            <span className="inline-block bg-white/15 border border-white/25 text-white/90 text-xs font-semibold px-3 py-1.5 rounded-full mb-6 tracking-wide uppercase">
              Fisheries Sector Capacity & Market Access Portal
            </span>
            <h1 className="text-4xl lg:text-6xl font-headline font-bold leading-tight mb-6">
              Empowering the Indian<br />
              <span className="text-accent">Fisheries Sector</span><br />
              Through Knowledge
            </h1>
            <p className="text-white/80 text-lg lg:text-xl leading-relaxed mb-10 max-w-2xl">
              FISCMAP is the national e-learning and market access platform built for fish farmers, students, entrepreneurs, and technicians — delivering structured training, verified credentials, and direct market connections.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link href="/signup" className="inline-flex items-center gap-2 bg-accent text-white font-headline font-semibold px-7 py-3.5 rounded-xl hover:bg-accent/90 transition-all shadow-lg hover:shadow-xl">
                Start Learning Free
                <Icon name="ArrowRightIcon" size={18} variant="outline" />
              </Link>
              <Link href="/fisheries-courses" className="inline-flex items-center gap-2 bg-white/15 border border-white/30 text-white font-headline font-semibold px-7 py-3.5 rounded-xl hover:bg-white/25 transition-all backdrop-blur-sm">
                Browse Courses
              </Link>
            </div>
          </div>
        </div>

        {/* Stats bar */}
        <div className="relative bg-white/10 backdrop-blur-sm border-t border-white/15">
          <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {STATS.map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-3xl font-headline font-bold text-accent">{s.value}</p>
                <p className="text-white font-semibold text-sm mt-0.5">{s.label}</p>
                <p className="text-white/55 text-xs mt-0.5 hidden lg:block">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── About ── */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <span className="text-primary font-semibold text-sm uppercase tracking-widest">About FISCMAP</span>
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mt-3 mb-6 leading-snug">
              A Purpose-Built Platform for the Fisheries Community
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-5">
              FISCMAP — the <strong className="text-foreground">Fisheries Sector Capacity and Market Access Portal</strong> — was developed to address a critical gap: the fisheries sector is one of India's most vital industries, yet its workforce has historically lacked access to structured, accredited, and practical training.
            </p>
            <p className="text-muted-foreground leading-relaxed mb-5">
              The platform consolidates learning resources, certification pathways, mentorship networks, and market intelligence into a single, accessible portal. Whether you are a small-scale fish farmer in a coastal village or a fisheries graduate entering the workforce, FISCMAP meets you where you are.
            </p>
            <p className="text-muted-foreground leading-relaxed">
              Courses are developed in partnership with government agencies, academic institutions, and industry practitioners — ensuring content is technically sound, locally relevant, and immediately applicable.
            </p>
          </div>

          {/* Bento-style info tiles */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2 bg-gradient-to-br from-primary to-secondary rounded-2xl p-6 text-white">
              <Icon name="AcademicCapIcon" size={28} variant="solid" className="text-accent mb-3" />
              <h3 className="font-headline font-bold text-lg mb-1">Government-Backed Curriculum</h3>
              <p className="text-white/70 text-sm leading-relaxed">Developed with ICAR, NFDB, and state fisheries universities to meet national fisheries competency standards.</p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
              <span className="text-2xl mb-2 block">🇮🇳</span>
              <h3 className="font-headline font-bold text-foreground text-sm mb-1">Multilingual Content</h3>
              <p className="text-muted-foreground text-xs leading-relaxed">All materials in English and Indian regional languages for maximum reach.</p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-5">
              <span className="text-2xl mb-2 block">🔗</span>
              <h3 className="font-headline font-bold text-foreground text-sm mb-1">Blockchain Credentials</h3>
              <p className="text-muted-foreground text-xs leading-relaxed">Tamper-proof certificates verifiable by any employer.</p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
              <span className="text-2xl mb-2 block">📊</span>
              <h3 className="font-headline font-bold text-foreground text-sm mb-1">Live Market Data</h3>
              <p className="text-muted-foreground text-xs leading-relaxed">Price trends, buyer listings, and export opportunities updated in real time.</p>
            </div>
            <div className="bg-purple-50 border border-purple-200 rounded-2xl p-5">
              <span className="text-2xl mb-2 block">🎯</span>
              <h3 className="font-headline font-bold text-foreground text-sm mb-1">Role-Adaptive Learning</h3>
              <p className="text-muted-foreground text-xs leading-relaxed">Your dashboard and recommendations adapt to your role and progress.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="bg-muted py-20">
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-14">
            <span className="text-primary font-semibold text-sm uppercase tracking-widest">Platform Capabilities</span>
            <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mt-3">
              Everything the Fisheries Sector Needs
            </h2>
            <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
              From first-time learners to seasoned professionals, FISCMAP provides the tools, content, and connections to grow.
            </p>
          </div>

          {/* Asymmetric bento grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <div
                key={f.title}
                className={`bg-card rounded-2xl p-6 border border-border hover:shadow-brand transition-shadow duration-300 ${i === 0 ? 'lg:col-span-2' : ''}`}
              >
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${f.color}`}>
                  <Icon name={f.icon as any} size={22} variant="solid" />
                </div>
                <h3 className="font-headline font-bold text-foreground text-lg mb-2">{f.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Who Is It For ── */}
      <section className="max-w-7xl mx-auto px-4 lg:px-8 py-20">
        <div className="text-center mb-14">
          <span className="text-primary font-semibold text-sm uppercase tracking-widest">Who It's For</span>
          <h2 className="text-3xl lg:text-4xl font-headline font-bold text-foreground mt-3">
            Built for Every Role in the Value Chain
          </h2>
          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            FISCMAP recognises that the fisheries sector is diverse. The platform adapts its content, tools, and recommendations to your specific role.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {AUDIENCES.map((a) => (
            <Link
              key={a.role}
              href={a.href}
              className={`rounded-2xl border-2 p-6 flex flex-col hover:shadow-brand-lg transition-all duration-300 group ${a.color}`}
            >
              <span className="text-4xl mb-4">{a.icon}</span>
              <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full w-fit mb-3 ${a.badge}`}>{a.role}</span>
              <p className="text-foreground/80 text-sm leading-relaxed flex-1">{a.desc}</p>
              <div className="mt-5 flex items-center gap-1 text-primary font-semibold text-sm group-hover:gap-2 transition-all">
                Explore <Icon name="ArrowRightIcon" size={15} variant="outline" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bg-gradient-to-br from-primary to-ocean-teal py-20">
        <div className="max-w-3xl mx-auto px-4 text-center text-white">
          <h2 className="text-3xl lg:text-4xl font-headline font-bold mb-5">
            Ready to Grow Your Fisheries Career?
          </h2>
          <p className="text-white/75 text-lg mb-10 leading-relaxed">
            Join thousands of fisheries professionals already learning, earning certificates, and accessing new markets through FISCMAP.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link href="/signup" className="inline-flex items-center gap-2 bg-accent text-white font-headline font-semibold px-8 py-4 rounded-xl hover:bg-accent/90 transition-all shadow-lg text-base">
              Create Free Account
              <Icon name="ArrowRightIcon" size={18} variant="outline" />
            </Link>
            <Link href="/login" className="inline-flex items-center gap-2 bg-white/15 border border-white/30 text-white font-headline font-semibold px-8 py-4 rounded-xl hover:bg-white/25 transition-all text-base">
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="bg-card border-t border-border py-8">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center space-x-2">
            <div className="w-7 h-7 bg-primary rounded-md flex items-center justify-center">
              <svg width="16" height="16" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14 2C7.373 2 2 7.373 2 14s5.373 12 12 12 12-5.373 12-12S20.627 2 14 2zm0 3c1.657 0 3 1.343 3 3s-1.343 3-3 3-3-1.343-3-3 1.343-3 3-3zm0 16c-3.314 0-6-2.686-6-6 0-.768.156-1.5.438-2.172C9.844 13.594 11.782 14 14 14s4.156-.406 5.562-1.172c.282.672.438 1.404.438 2.172 0 3.314-2.686 6-6 6z" fill="white"/>
              </svg>
            </div>
            <span className="font-headline font-bold text-foreground text-sm">FISCMAP Portal</span>
          </div>
          <p className="text-muted-foreground text-xs text-center">
            © 2026 FISCMAP Portal. Fisheries Sector Capacity and Market Access Portal. All rights reserved.
          </p>
          <div className="flex gap-5 text-xs text-muted-foreground">
            <Link href="/login" className="hover:text-primary transition-colors">Sign In</Link>
            <Link href="/signup" className="hover:text-primary transition-colors">Register</Link>
            <Link href="/fisheries-courses" className="hover:text-primary transition-colors">Courses</Link>
          </div>
        </div>
      </footer>

    </div>
  );
}
